import { createSelector } from "reselect";
import { deepEqual, parseSelectedColumns } from "../utils/misc";
import { stringSort } from "../utils/sort";
import { SORT_DIRECTIONS } from "../constant/sort";

const selectState = (state) => state;

export const selectNetworkActions = createSelector(selectState, (state) => state && state.networkActions);

export const selectIsFetching = (actions) =>
  createSelector(selectNetworkActions, (networkActions) => {
    const pandingNetworkActions = Object.keys(networkActions)
      .filter((actionName) => networkActions[actionName].isFetching)
      .filter((action) => actions.indexOf(action) >= 0);

    return pandingNetworkActions.length > 0;
  });

export const selectNetworkActionsError = (actions) =>
  createSelector(selectNetworkActions, (networkActions) => {
    const pandingNetworkActions = Object.keys(networkActions)
      .filter((actionType) => actions.includes(actionType))
      .reduce((obj, key) => {
        obj[key] = networkActions[key];
        return obj;
      }, {});

    const fetchingNetworkAction = Object.values(pandingNetworkActions).filter(
      (networkAction) => !networkAction.isFetching && (networkAction.msg || networkAction.code)
    );

    return fetchingNetworkAction;
  });

export const selectSettings = createSelector(selectState, (state) => state && state.settings);

export const selectPatients = createSelector(selectState, (state) => state && state.patients);

export const selectSearchedPatients = createSelector(selectState, (state) => state && state.searchedPatients);

export const selectPatientCycles = createSelector(selectState, (state) => state && state.patientCycles);

export const selectPatientNotes = createSelector(selectState, (state) => state && state.patientNotes);

export const selectPatientAttachments = createSelector(selectState, (state) => state && state.patientAttachments);

export const selectPatientTasks = createSelector(selectState, (state) => state && state.patientTasks);

export const selectUser = createSelector(selectState, (state) => state && state.user);

export const selectIsPhiAllowed = createSelector(selectUser, (user) => user && user.isPhiAllowed);

export const selectUserId = createSelector(selectUser, (user) => user && user.id);

export const selectNotificationPreferences = createSelector(
  selectState,
  (state) => state && state.notificationPreferences
);

export const selectEnvironment = createSelector(selectState, (state) => state && state.environment);

export const selectUnits = createSelector(selectState, (state) => state && state.units);

export const selectPatientTableColumns = createSelector(selectState, (state) => state && state.patientTableColumns);

export const selectAssignees = createSelector(selectState, (state) => state && state.assignees);

export const selectUsers = createSelector(selectState, (state) => state && state.users);

export const selectUserGroups = createSelector(selectState, (state) => state && state.userGroups);

export const selectUserGroupsWithUsers = createSelector(selectState, (state) => state && state.userGroupsWithUsers);

export const selectAssigneesAndUserGroups = createSelector(
  [selectAssignees, selectUserGroups],
  (assignees, userGroups) => {
    const ASSIGNEE_TYPE = 1;
    const USER_GROUP_TYPE = 2;

    if ((!assignees || !Object.values(assignees).length) && (!userGroups || !Object.values(userGroups).length))
      return [];
    const parsedAssignees = Object.values(assignees);
    parsedAssignees.forEach((assignee) => {
      assignee.displayName = `${assignee.lastName} ${assignee.firstName}`;
      assignee.assigneeType = ASSIGNEE_TYPE;
      assignee.type = "user";
    });

    const parsedUserGroups = Object.values(userGroups);
    parsedUserGroups.forEach((userGroup) => {
      userGroup.displayName = userGroup.name;
      userGroup.assigneeType = USER_GROUP_TYPE;
      userGroup.type = "user-group";
    });

    const assigneesAndUserGroups = parsedAssignees
      .concat(parsedUserGroups)
      .sort((itemA, itemB) => stringSort(itemA.displayName, itemB.displayName, SORT_DIRECTIONS));

    return assigneesAndUserGroups;
  }
);

/**
 * Selector that combines ALL users (including unassignable) with user groups
 * This is used for saved lists where viewers can include unassignable users
 */
export const selectAllUsersAndUserGroups = createSelector([selectUsers, selectUserGroups], (users, userGroups) => {
  const ASSIGNEE_TYPE = 1;
  const USER_GROUP_TYPE = 2;

  if ((!users || !Object.values(users).length) && (!userGroups || !Object.values(userGroups).length)) return [];

  const parsedUsers = Object.values(users);
  parsedUsers.forEach((user) => {
    user.displayName = `${user.lastName} ${user.firstName}`;
    user.assigneeType = ASSIGNEE_TYPE;
    user.type = "user";
  });

  const parsedUserGroups = Object.values(userGroups);
  parsedUserGroups.forEach((userGroup) => {
    userGroup.displayName = userGroup.name;
    userGroup.assigneeType = USER_GROUP_TYPE;
    userGroup.type = "user-group";
  });

  const allUsersAndUserGroups = parsedUsers
    .concat(parsedUserGroups)
    .sort((itemA, itemB) => stringSort(itemA.displayName, itemB.displayName, SORT_DIRECTIONS));

  return allUsersAndUserGroups;
});

export const selectTracking = createSelector(selectState, (state) => state && state.tracking);

export const selectFilters = createSelector(selectState, (state) => state && state.filters);

export const selectKeepEditingCycleMode = createSelector(selectState, (state) => state && state.keepEditingCycleMode);

export const selectSite = createSelector(
  selectState,
  (state) => state && state.settings && state.settings.id_prefix && state.settings.id_prefix.value
);

export const selectRxInstructionSettings = createSelector(
  selectState,
  (state) => state && state.settings && state.settings[["Pharmacy.ShowRxInstruction"]]
);

export const selectPatientModal = createSelector(selectState, (state) => state && state.patientModal);

export const selectIdPatientModal = createSelector(selectState, (state) => state && state.idPatientModal);

export const selectSearchedPatient = createSelector(selectState, (state) => state && state.searchedPatient);

export const selectIDPConfig = createSelector(selectState, (state) => state && state.idpConfig);
export const selectOktaClientId = createSelector(selectState, (state) => state && state.oktaClientId);

export const selectCalendarViewType = createSelector(selectState, (state) => state && state.calendarViewType);
export const selectCurrentCycle = createSelector(selectState, (state) => state && state.currentCycle);
export const selectAPSponsors = createSelector(selectState, (state) => state && state.APSponsors);
export const selectTotalPatients = createSelector(selectState, (state) => state && state.totalPatients);
export const selectCurrentPage = createSelector(selectState, (state) => state && state.currentPage);
export const selectSearchedText = createSelector(selectState, (state) => state && state.searchText);
export const selectSorting = createSelector(selectState, (state) => state && state.sorting);
export const selectOrdersTableSearchedText = createSelector(
  selectState,
  (state) => state && state.ordersTableSearchText
);
export const selectEnableFlash = createSelector(selectState, (state) => state && state.enableFlash);
export const selectSearchResultCurrentPage = createSelector(
  selectState,
  (state) => state && state.searchResultCurrentPage
);
export const selectViewType = createSelector(selectState, (state) => state && state.viewType);
export const selectIsLoadingEnabled = createSelector(selectState, (state) => state && state.isLoadingEnabled);
export const selectIsFilterListFormVisiable = createSelector(selectState, (state) => state && state.isVisiable);
export const selectFilterList = createSelector(selectState, (state) => state && state.filterList);
export const selectedSavedListFilters = createSelector(selectState, (state) => state && state.savedListFilters);
export const selectedFilterListQuery = createSelector(selectedSavedListFilters, (filters) => filters && filters.query);
export const selectSavedListData = createSelector(selectState, (state) => state && state.tableData);
export const selectedFilterListType = createSelector(
  selectedSavedListFilters,
  (filters) => filters && filters.filterBy
);
const selectedSavedListSortingStatues = createSelector(
  selectedSavedListFilters,
  (filters) => filters && filters.sortingStatuses
);
export const selectedFilterSorting = createSelector(
  selectedSavedListSortingStatues,
  selectedFilterListType,
  (sortingStatuses, listType) => sortingStatuses && listType && sortingStatuses[listType]
);
export const selectedSavedListActiveColumn = createSelector(
  selectedFilterSorting,
  (selectedFilterSorting) =>
    selectedFilterSorting && {
      name: selectedFilterSorting.orderBy,
      sortBy: selectedFilterSorting[selectedFilterSorting.orderBy]
    }
);
export const selectSavedListTableColumns = createSelector(selectState, (state) => state && state.savedListColumns);
export const selectFormPopups = createSelector(selectState, (state) => state && state.formPopups);
export const selectFilterListStatuses = createSelector(selectState, (state) => state && state.filterListStatuses);
export const selectIsSavedListInit = createSelector(selectState, (state) => state && state.isSavedListinitialized);
export const selectIsFilterListFiltersModified = createSelector(
  selectFilters,
  selectFilterList,
  (filters, filtersList) => {
    return filters && filtersList?.params?.filters && !deepEqual(filters, filtersList.params.filters);
  }
);
export const selectTableColumnsModified = createSelector(
  selectPatientTableColumns,
  selectFilterList,
  (patientTableColumns, filtersList) => {
    const newTableColumns = JSON.parse(JSON.stringify(patientTableColumns));
    filtersList?.params?.selectedColumns &&
      patientTableColumns &&
      parseSelectedColumns(newTableColumns, filtersList.params.selectedColumns);
    return !deepEqual(newTableColumns, patientTableColumns);
  }
);
export const selectTableSorterModified = createSelector(selectSorting, selectFilterList, (sorting, filtersList) => {
  return sorting && filtersList?.params?.sorting && !deepEqual(sorting, filtersList.params.sorting);
});
const filterSelectedColumns = (tableColumns) => {
  const userSelectedColumns = [];

  tableColumns &&
    Object.values(tableColumns).forEach((value) => {
      value.display && !value.mandatory ? userSelectedColumns.push(value.key) : "";
    });
  return userSelectedColumns;
};
export const getFilterListSelectedColumns = createSelector(
  selectSavedListTableColumns,
  selectFilterList,
  (tableColumns, filtersList) => filterSelectedColumns(tableColumns, tableColumns[filtersList?.selectedView])
);

export const getUserSelectedColumns = createSelector(selectPatientTableColumns, (patientTableColumns) =>
  filterSelectedColumns(patientTableColumns)
);
export const selectViewTablesData = createSelector(selectState, (state) => (state && state.tablesData) || {});
export const selectSavedListViewType = createSelector(selectState, (state) => state && state.savedListViewType);
export const selectCalendarOrderDateFilter = createSelector(
  selectState,
  (state) => state && state.calendarOrderDateFilter
);
export const selectCanViewOrDownloadPatientAttachments = createSelector(
  selectState,
  (state) => state && state.canViewOrDownloadPatientAttachments
);
export const selectMultiples = createSelector(selectState, (state) => state && state.multiples);

export const selectCSVPatientReportData = createSelector(selectState, (state) => state && state.csvPatientReportData);

export const selectCycleAdminDateFilter = createSelector(selectState, (state) => state && state.cycleAdminDateFilter);

export const selectFilteredPatientCycles = createSelector(selectState, (state) => state && state.filteredPatientCycles);

export const selectIsAddingPatientCycle = createSelector(selectState, (state) => state && state.isAddingPatientCycle);

export const selectSelectedOrderId = createSelector(selectState, (state) => state && state.selectedOrderId);

export const selectSelectedOrder = createSelector(
  [selectPatientCycles, selectSelectedOrderId],
  (cycles, selectedOrderId) => {
    if (!selectedOrderId || !cycles || !cycles.length) {
      return null;
    }
    return cycles.find((cycle) => cycle.id === selectedOrderId) || null;
  }
);
